<template>
  <nav
    class="-mr-lg pr-lg"
    @keydown.left.esc="closeExtendedMenu"
  >
    <ul class="space-y-sm">
      <li
        v-for="(reportSection, index) in reportSections"
        :key="index"
        class="group -mx-base"
      >
        <button
          :ref="(el) => Object.assign(topLevelMenuItem, { [`${index}`]: el })"
          :class="[
            'w-full text-sm sm:text-base rounded-md px-sm md:px-base py-md flex flex-shrink-0 items-center outline-none focus-visible:ring-2 ring-primary-500',
            activeTab(reportSection.id, route().current()) ? 'active-tab' : 'tab',
            focusedTab(reportSection.id, route().current()) ? 'selected-tab' : '',
          ]"
          :style="tabStyles"
          :aria-label="`${reportSection.label}. ${extendedMenuItems.length} options to choose from. Use arrow keys to navigate`"
          @click="navigateToSection(reportSection.id)"
          @keydown.enter="navigateToSection(reportSection.id)"
          @mousemove="openExtendedMenuOnHover(reportSection, index)"
          @mouseleave="clearSelectedTabTimeout"
          @focus="openExtendedMenu(reportSection, index)"
          @keydown.right="enterExtendedMenu"
          @keydown.down.prevent="index === reportSections.length - 1 ? topLevelMenuItem?.[0].focus() : topLevelMenuItem?.[index + 1].focus()"
          @keydown.up.prevent="index === 0 ? topLevelMenuItem?.[reportSections.length - 1].focus() : topLevelMenuItem?.[index - 1].focus()"
          @keydown.tab="index === reportSections.length - 1 ? reportStore.setSelectedTab(null) : undefined"
          @keydown.tab.shift.exact="index === 0 ? reportStore.setSelectedTab(null) : undefined"
        >
          <FontAwesomeIcon
            aria-hidden="true"
            :icon="reportSection.icon"
            class="w-5 flex justify-start mr-base"
            :style="tabStyles"
          />
          {{ reportSection.label }}
          <FontAwesomeIcon
            aria-hidden="true"
            :icon="faChevronRight"
            class="w-4 ml-auto"
            :style="tabStyles"
          />
        </button>
      </li>
    </ul>
    <ul
      v-if="showSubMenu"
      :class="[
        'absolute w-72 px-lg bottom-0 -ml-[1px] space-y-xxs py-base z-40 flex flex-col justify-start gap-xxs border-r border-l border-default-300 shadow-sm bg-white',
        'text-nowrap bg-default-50 rounded-r-md overflow-y-scroll',
        route().current('explore-data') ? 'h-layout-dashboard top-0 left-80' : 'h-screen top-0 left-72',
      ]"
    >
      <li
        class="h-0"
        aria-hidden="true"
      >
        <button @focus="extendedMenuItem?.[extendedMenuItems.length - 1].focus()" />
      </li>
      <li
        v-for="(menuItem, index) in extendedMenuItems"
        :key="menuItem.id"
        class="group"
      >
        <button
          v-if="reportStore.selectedTab"
          :ref="(el) => Object.assign(extendedMenuItem, { [`${index}`]: el })"
          class="w-full outline-none focus-visible:ring-2 ring-primary-500 py-sm px-base rounded-md text-black text-left text-sm font-medium leading-tight sub-menu-section"
          :style="tabStyles"
          :aria-label="menuItem.name"
          @click="navigateToSection(reportStore.selectedTab, menuItem.id)"
          @keydown.up.prevent="index === 0 ? extendedMenuItem?.[extendedMenuItems.length - 1].focus() : extendedMenuItem?.[index - 1].focus()"
          @keydown.down.prevent="index === extendedMenuItems.length - 1 ? extendedMenuItem?.[0].focus() : extendedMenuItem?.[index + 1].focus()"
        >
          {{ menuItem.name }}
        </button>
      </li>
      <li
        class="h-0"
        aria-hidden="true"
      >
        <button @focus="extendedMenuItem?.[0].focus()" />
      </li>
    </ul>
  </nav>
</template>

<script setup lang="ts">
import { route } from 'ziggy-js';
import { computed, ref } from 'vue';
import { router } from '@inertiajs/vue3';
import {
  useReportStore, OrderableTabs, OrderableSubTabs,
} from '@/Stores/Report';
import { useComparablesStore } from '@/Stores/Comparables';
import usePageScroll from '@/Composables/usePageScroll';
import ReportSectionOrder from '@/Config/ReportSectionOrder';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
  faChevronRight, faHouseHeart, faRetweet, faMapLocationDot, faPhotoFilm, faChartMixed, faAddressCard,
} from '@awesome.me/kit-644ec404bf/icons/classic/regular';
import { sortBy } from 'lodash-es';
import { useLocalAreaStore } from '@/Stores/LocalArea';
import BrandSettingsData = App.Data.Settings.BrandSettingsData;
import ReportDigitalDesignContentData = App.Data.ReportDesign.Digital.ReportDigitalDesignContentData;
import ReportTab = App.Enums.ReportTab;

const topLevelMenuItem = ref<HTMLElement[]>([]);

const extendedMenuItem = ref<HTMLElement[]>([]);

const reportStore = useReportStore();
const comparablesStore = useComparablesStore();
const localAreaStore = useLocalAreaStore();

const { smoothScroll } = usePageScroll();

const props = defineProps<{
  brandSettings?: BrandSettingsData;
  reportDigitalDesignContentData?: ReportDigitalDesignContentData;
}>();

const selectedTabIndex = ref(0);

const selectedTabTimeout = ref();

type ReportSectionType = {
  id: ReportTab;
  label: string;
  order?: number;
  display: boolean;
  icon: string;
};

const showSubMenu = computed(() => {
  if (!reportStore.selectedTab) {
    return false;
  }

  if (reportStore.selectedTab === 'gallery') {
    return !(reportStore.gallery.isEmpty);
  }

  return true;
});

const setTab = async (tab: ReportTab) => {
  reportStore.setCurrentTab(tab);
};

const scrollToElement = async (tab: ReportTab, section?: keyof OrderableSubTabs) => {
  await setTab(tab);

  if (!section) {
    return;
  }

  if (tab !== 'comparables' && tab !== 'localArea') {
    smoothScroll(document.getElementById(section));
    return;
  }

  if (tab === 'comparables') {
    await comparablesStore.updateComparableType(section);
    return;
  }

  localAreaStore.updateSelectedTabByName(section);
};

const navigateToSection = async (tab: ReportTab, section?: keyof OrderableSubTabs) => {
  if (typeof window !== 'undefined' && reportStore.isPublicReport && !window.location.search.includes('?hideCover=1')) {
    router.visit('?hideCover=1', {
      onSuccess: () => {
        scrollToElement(tab, section);
      },
    });

    return;
  }

  scrollToElement(tab, section);
};

const extendedMenuItems = computed(() => {
  const subSections = ReportSectionOrder.find((tab) => tab.id === reportStore.selectedTab)?.subsections;

  if (!reportStore.selectedTab || !subSections) {
    return [];
  }

  const sortedSections = subSections.sort((a, b) => (a.id > b.id ? 1 : 0));

  const filteredSubTabs: { id: string, name: string, subsections?: any }[] = sortedSections.filter((section) => (reportStore.content[reportStore.selectedTab as keyof OrderableTabs][section.id as keyof OrderableSubTabs] as any).display);

  if (reportStore.selectedTab === 'market') {
    const subsections = filteredSubTabs.map((subTab) => subTab.subsections).flat();
    const salesSections = sortBy(subsections.filter((subsection) => (subsection.name).includes('sales')), 'name');
    const lettingsSections = sortBy(subsections.filter((subsection) => (subsection.name).includes('lettings')), 'name');
    return [...salesSections, ...lettingsSections];
  }

  if (reportStore.selectedTab === 'gallery') {
    return filteredSubTabs.filter((subTab) => !reportStore.gallery[subTab.id as 'pastListingImages' | 'uploadedImages' | 'videoAndVirtualTours'].isEmpty);
  }

  return filteredSubTabs;
});

const openExtendedMenu = async (reportSection: ReportSectionType, index: number) => {
  reportStore.setSelectedTab(reportSection.id);
  selectedTabIndex.value = index;
};

const enterExtendedMenu = () => {
  extendedMenuItem.value?.[0].focus();
};

const closeExtendedMenu = async () => {
  topLevelMenuItem.value?.[selectedTabIndex.value].focus();
};

const openExtendedMenuOnHover = (reportSection: ReportSectionType, index: number) => {
  clearTimeout(selectedTabTimeout.value);
  selectedTabTimeout.value = setTimeout(() => openExtendedMenu(reportSection, index), 20);
};

const clearSelectedTabTimeout = () => {
  clearTimeout(selectedTabTimeout.value);
};

const reportSections = computed(() => [
  {
    id: 'overview',
    label: 'Property Data',
    order: reportStore.overview?.order,
    display: reportStore.getSectionDisplay('overview'),
    icon: faHouseHeart,
  },
  {
    id: 'comparables',
    label: 'Comparables',
    order: reportStore.comparables.order,
    display: reportStore.getSectionDisplay('comparables'),
    icon: faRetweet,
  },
  {
    id: 'localArea',
    label: 'Local Area',
    order: reportStore.localArea?.order,
    display: reportStore.getSectionDisplay('localArea'),
    icon: faMapLocationDot,

  },
  {
    id: 'gallery',
    label: 'Gallery',
    order: reportStore.gallery.order,
    display: reportStore.getSectionDisplay('gallery'),
    icon: faPhotoFilm,
  },
  {
    id: 'market',
    label: 'Market Data',
    order: reportStore.market.order,
    display: reportStore.getSectionDisplay('market'),
    icon: faChartMixed,
  },
  {
    id: 'about',
    label: 'About',
    order: reportStore.about.order,
    display: reportStore.isPublicReport,
    icon: faAddressCard,
  },
].filter((reportSection) => reportSection.display).sort((a, b) => (a.order - b.order)) as ReportSectionType[]);

const tabStyles = ref({
  '--fontFamily': `${props.brandSettings ? props.brandSettings.text.body.fontFamily : 'inter'}`,
  '--textColor': '#374151',
  '--textColorHover': '#3730a3',
  '--mixColorBackgroundHover': 'white 75%',
  '--brandColor': '#D1DCFF',
});

const backgroundColours = computed(() => {
  if (!props.brandSettings || !props.reportDigitalDesignContentData) {
    return '#000000';
  }

  return props.reportDigitalDesignContentData.reportColors.colourStyle === 'custom'
    ? props.reportDigitalDesignContentData.reportColors.customColour
    : props.brandSettings.colours[props.reportDigitalDesignContentData.reportColors.colourStyle as 'primary' | 'secondary'];
});

switch (props.reportDigitalDesignContentData?.reportColors.style) {
  case 'brand-light':
    tabStyles.value = {
      '--fontFamily': `${props.brandSettings?.text.body.fontFamily}`,
      '--textColor': `${props.reportDigitalDesignContentData.reportColors.menuTextTextColour}`,
      '--textColorHover': `${props.reportDigitalDesignContentData.reportColors.menuTextSelectedColour}`,
      '--mixColorBackgroundHover': '#FFFFFFBF', // white 75%
      '--brandColor': backgroundColours.value,
    };
    break;
  case 'brand-bold':
    tabStyles.value = {
      '--fontFamily': `${props.brandSettings?.text.body.fontFamily}`,
      '--textColor': `${props.reportDigitalDesignContentData.reportColors.menuTextTextColour}`,
      '--textColorHover': `${props.reportDigitalDesignContentData.reportColors.menuTextSelectedColour}`,
      '--mixColorBackgroundHover': '#FFFFFF4D', // white 30%
      '--brandColor': backgroundColours.value,
    };
    break;
  default:
    break;
}

const activeTab = (tab: string, routeName?: string) => {
  switch (routeName) {
    case 'explore-data':
      return reportStore.currentTab === tab || reportStore.selectedTab === tab;
    case 'public.reports.show':
      return reportStore.currentTab === tab && reportStore.hideCover;
    default:
      return false;
  }
};

const focusedTab = (tab: string, routeName?: string) => {
  switch (routeName) {
    case 'explore-data':
      return reportStore.currentTab !== tab && reportStore.selectedTab === tab;
    case 'public.reports.show':
      return reportStore.currentTab !== tab && reportStore.selectedTab === tab && reportStore.hideCover;
    default:
      return false;
  }
};
</script>

<style scoped>
.tab {
  color: var(--textColor);
  font-family: var(--fontFamily);
}

.tab:active {
  color: var(--textColorHover);
  background: color-mix(in srgb, white 50%, var(--brandColor));
}

.active-tab {
  color: var(--textColorHover);
  background: color-mix(in srgb, white 30%, var(--brandColor));
  font-family: var(--fontFamily);
}

.selected-tab {
  color: var(--textColorHover);
  background: color-mix(in srgb, var(--mixColorBackgroundHover), var(--brandColor));
  font-family: var(--fontFamily);
}

.sub-menu-section {
  font-family: var(--fontFamily);
}

.sub-menu-section:hover {
  background: color-mix(in srgb, white 85%, var(--brandColor));
}

.sub-menu-section:active {
  background: color-mix(in srgb, white 90%, var(--brandColor));
}
</style>
